import { createContext, use, useMemo, type ReactNode } from 'react';
import { PassageStateContext } from './passage-state-provider';
import { PassageDataContext } from '@/data/passage/passage-data-context';

/**
 * Provider component for passage navigation with derived state
 *
 * Provides both base passage state and computed/derived values,
 * such as looking up the current section from passageId.
 *
 * Must be wrapped inside both PassageStateProvider and SectionDataProvider.
 *
 * @example
 * ```tsx
 * import { PassageProvider } from '@/lib/context/passage-provider';
 *
 * function App() {
 *   return (
 *     <SectionDataProvider>
 *       <PassageStateProvider>
 *         <PassageProvider>
 *           <YourAppComponents />
 *         </PassageProvider>
 *       </PassageStateProvider>
 *     </SectionDataProvider>
 *   );
 * }
 * ```
 */
interface PassageProviderProps {
    children: ReactNode;
}

export interface PassageContextType {
    /** Current section (looked up from passageId), null if no passageId or section not found */
    section: Passage | null;
    /** True if still waiting for initial data from PassageStateContext or SectionDataContext */
    isLoading: boolean;
}

export const PassageContext = createContext<PassageContextType | undefined>(undefined);

export function PassageProvider({ children }: PassageProviderProps) {
    const passageStateContext = use(PassageStateContext);
    const { data: passageData, get: getPassage } = use(PassageDataContext);

    if (!passageStateContext) {
        throw new Error('PassageProvider must be used within a PassageStateProvider');
    }

    const { passageId } = passageStateContext;

    // Check if we're still waiting for initial data
    const isLoading = useMemo(() => {
        // If we have a passageId but no data yet, we're loading
        if (passageId && !passageData?.items) {
            return true;
        }
        return false;
    }, [passageId, passageData?.items]);

    const section = useMemo(() => {
        if (!passageId || !passageData?.items) {
            return null;
        }
        return getPassage(passageId);
    }, [passageId, passageData?.items, getPassage]);

    return (
        <PassageContext.Provider value={{ section, isLoading }}>
            {children}
        </PassageContext.Provider>
    );
}

